"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { SiteFooter, SiteHeader } from "@/components/SiteChrome";

export default function LoginClient() {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);
  const [demoUrl, setDemoUrl] = useState<string | null>(null);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [mode, setMode] = useState<"magic" | "password">("magic");

  // Avoid hydration mismatches from password managers / extensions mutating inputs.
  useEffect(() => {
    setMounted(true);
  }, []);

  async function onMagic(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setMagicSent(false);
    setDemoUrl(null);
    setDeliveryError(null);
    const fd = new FormData(e.currentTarget);
    const res = await fetch("/api/auth/magic-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: fd.get("email") }),
    });
    const data = await res.json();
    if (!res.ok) {
      setError(data.error?.message ?? "Could not send link");
      return;
    }
    setMagicSent(true);
    if (typeof data.demo_activate_url === "string") {
      setDemoUrl(data.demo_activate_url);
    }
    if (typeof data.delivery_error === "string") {
      setDeliveryError(data.delivery_error);
    }
  }

  async function onPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: fd.get("email"),
        password: fd.get("password"),
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      setError(data.error?.message ?? "Login failed");
      return;
    }
    router.push(data.redirect);
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-md flex-1 px-4 py-10">
        <h1 className="font-[family-name:var(--font-display)] text-4xl">
          Login
        </h1>
        {!mounted ? (
          <p className="mt-6 text-sm text-[var(--muted)]">Loading…</p>
        ) : (
          <>
            <div className="mt-4 flex gap-3 text-sm">
              <button
                type="button"
                className={mode === "magic" ? "font-semibold underline" : ""}
                onClick={() => setMode("magic")}
              >
                Email magic link
              </button>
              <button
                type="button"
                className={mode === "password" ? "font-semibold underline" : ""}
                onClick={() => setMode("password")}
              >
                Staff password
              </button>
            </div>

            {mode === "magic" ? (
              <form
                onSubmit={onMagic}
                className="mt-6 grid gap-3 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-4"
              >
                <p className="text-sm text-[var(--muted)]">
                  Customers: enter the email on your account. We email a one-time
                  login link when delivery succeeds.
                </p>
                <label className="grid gap-1 text-sm">
                  Email
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    suppressHydrationWarning
                    className="rounded border border-[var(--line)] px-3 py-2"
                  />
                </label>
                {magicSent && !demoUrl && (
                  <p className="text-sm text-[var(--accent)]">
                    If that email has an account, a login link was emailed.
                  </p>
                )}
                {demoUrl && (
                  <div className="rounded-md border border-[var(--accent)]/40 bg-[var(--surface)] p-3 text-sm">
                    <p className="font-medium text-[var(--ink)]">
                      {deliveryError
                        ? "Email could not be delivered — use this link:"
                        : "Email sending is not configured yet — use this link:"}
                    </p>
                    {deliveryError && (
                      <p className="mt-1 text-[var(--danger)]">{deliveryError}</p>
                    )}
                    <a
                      href={demoUrl}
                      className="mt-2 block break-all text-[var(--accent)] underline"
                    >
                      {demoUrl}
                    </a>
                  </div>
                )}
                {error && (
                  <p className="text-sm text-[var(--danger)]">{error}</p>
                )}
                <button
                  type="submit"
                  className="rounded-md bg-[var(--accent)] px-4 py-3 text-white"
                >
                  Email me a login link
                </button>
              </form>
            ) : (
              <form
                onSubmit={onPassword}
                className="mt-6 grid gap-3 rounded-lg border border-[var(--line)] bg-[var(--panel)] p-4"
              >
                <p className="text-sm text-[var(--muted)]">
                  For crew and owners with a staff account.
                </p>
                <label className="grid gap-1 text-sm">
                  Email
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="username"
                    suppressHydrationWarning
                    className="rounded border border-[var(--line)] px-3 py-2"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  Password
                  <input
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    suppressHydrationWarning
                    className="rounded border border-[var(--line)] px-3 py-2"
                  />
                </label>
                {error && (
                  <p className="text-sm text-[var(--danger)]">{error}</p>
                )}
                <button
                  type="submit"
                  className="rounded-md bg-[var(--accent)] px-4 py-3 text-white"
                >
                  Sign in
                </button>
              </form>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
